import React from "react";

import { black, blueGradient, lightTeal, white } from "./base/colors";
import Box from "./base/box";
import Image from "./base/image";
import Text from "./base/text";
import Link from "./base/link";
import Flex from "./base/flex";

import { useContentContext } from "../context/content-context";

import scPharmaLogo from "../images/scpharma-logo.svg";
import hcpScPharmaLogo from "../images/hcp-scpharma-logo.svg";
import youtube from "../images/youtube.svg";
import x from "../images/x.svg";
import xColor from "../images/x-color.svg";
import facebook from "../images/facebook.svg";
import facebookColor from "../images/facebook-color.svg";
import linkedin from "../images/linkedin.svg";
import linkedinColor from "../images/linkedin-color.svg";
import Navigation from "./navigation";
import Section from "./section";

const CONTENT = {
  patient: {
    logo: scPharmaLogo,
    sitemapUrl: "/sitemap",
    background: blueGradient,
    secondaryBackgroundColor: blueGradient,
    textColor: white,
    xIcon: x,
    facebookIcon: facebook,
    linkedinIcon: linkedin,
  },
  hcp: {
    logo: hcpScPharmaLogo,
    sitemapUrl: "/hcp/sitemap",
    background: white,
    textColor: black,
    xIcon: xColor,
    facebookIcon: facebookColor,
    linkedinIcon: linkedinColor,
    secondaryBackgroundColor: lightTeal,
  },
};

const TEXT = {
  esp: {
    rights: "Todos los derechos reservados.",
    sitemap: "Mapa del sitio",
    terms: "Términos de uso",
    privacy: "Política de privacidad",
  },
  eng: {
    rights: "All rights reserved.",
    sitemap: "Sitemap",
    terms: "Terms of Use",
    privacy: "Privacy Policy",
  },
};

const Footer = ({ variant }) => {
  const {
    logo,
    sitemapUrl,
    textColor,
    background,
    xIcon,
    facebookIcon,
    linkedinIcon,
    secondaryBackgroundColor,
  } = CONTENT[variant];

  const { language, isHCP } = useContentContext();
  const isSpanish = language === "esp";

  const { rights, sitemap, terms, privacy } = TEXT[language];

  return (
    <>
      <Section
        padding={
          isHCP
            ? ["33px 25px", "19px 32px 31px", "31px 32px 34px"]
            : ["24px 25px", "29px 32px 30px", "36px 32px 16px"]
        }
        background={secondaryBackgroundColor}
        height={variant === "hcp" ? ["fit-content", "165px", "160px"] : "unset"}
        as="nav"
        aria-label="secondary"
      >
        <Navigation variant="footer" />
      </Section>
      <Section
        as="footer"
        flexDirection="column"
        background={background}
        padding={
          isHCP
            ? ["42px 28px 37px", "29px 32px 37px", "38px 32px"]
            : ["4px 28px 36px", "0 32px 37px", "0 32px 35px"]
        }
        role="contentinfo"
      >
        <Flex
          flexDirection={["column", "row", "row"]}
          justifyContent="space-between"
        >
          <Box maxWidth={375} mb={["28px", 0, 0]}>
            <a
              target="_blank"
              rel="noopener noreferrer"
              href="https://www.scpharmaceuticals.com/"
              aria-label="opens main scPharmaceuticals site in a new tab"
            >
              <Image
                alt="scPharmaceuticals logo"
                width={243}
                mb={23}
                src={logo}
              />
            </a>
            <Text color={textColor} fontSize="14px">
              {isHCP
                ? `Copyright ©2025. ${rights} scPharmaceuticals.\nUS-FUR-22-00080 05/2025`
                : isSpanish
                ? `Copyright ©2025. ${rights} scPharmaceuticals.\nUS-FUR-24-00149 03/2025`
                : `Copyright ©2025. ${rights} scPharmaceuticals.\nUS-FUR-22-00083 06/2025`}
            </Text>
          </Box>
          <Flex
            flexDirection="column"
            justifyContent="space-between"
            alignItems={["flex-start", "flex-end", "flex-end"]}
          >
            <Flex mb={["23px", 0, 0]}>
              {!isHCP && (
                <a
                  target="_blank"
                  aria-label="opens Furoscix YouTube in a new tab"
                  rel="noopener noreferrer"
                  href="https://www.youtube.com/@furoscix"
                >
                  <Image
                    width={35}
                    height={25}
                    ml={[0, 13, 13]}
                    mr={[13, 0, 0]}
                    src={youtube}
                    alt="youtube"
                  />
                </a>
              )}

              <a
                target="_blank"
                rel="noopener noreferrer"
                aria-label="opens scPharmaUSA X account in a new tab"
                href="https://twitter.com/scPharmaUSA"
              >
                <Image
                  width={25}
                  height={25}
                  ml={[0, 13, 13]}
                  mr={[13, 0, 0]}
                  src={xIcon}
                  alt="x"
                />
              </a>

              <a
                target="_blank"
                aria-label="opens Furoscix Facebook in a new tab"
                rel="noopener noreferrer"
                href={
                  isHCP
                    ? "https://www.facebook.com/scpharmaceuticals?fref=ts&ref=br_tf"
                    : "https://www.facebook.com/furoscix/"
                }
              >
                <Image
                  width={25}
                  height={25}
                  ml={[0, 13, 13]}
                  mr={[13, 0, 0]}
                  src={facebookIcon}
                  alt="facebook"
                />
              </a>

              <a
                target="_blank"
                aria-label="opens scPharmaceuticals LinkedIn in a new tab"
                rel="noopener noreferrer"
                href="https://www.linkedin.com/company/scpharmaceuticals/"
              >
                <Image
                  width={25}
                  height={25}
                  ml={[0, 13, 13]}
                  mr={[13, 0, 0]}
                  src={linkedinIcon}
                  alt="linkedin"
                />
              </a>
            </Flex>
            <Flex>
              <Link
                href={isSpanish ? `/es${sitemapUrl}` : sitemapUrl}
                variant="subfooter"
                color={textColor}
                fontSize={[12, 12, 16]}
              >
                {sitemap}
              </Link>

              <Link
                as="a"
                target="_blank"
                rel="noopener noreferrer"
                href="https://www.scpharmaceuticals.com/terms-of-use/"
                variant="subfooter"
                color={textColor}
                fontSize={[12, 12, 16]}
                aria-label="opens terms of use page in a new tab"
              >
                {terms}
              </Link>
              <Link
                as="a"
                target="_blank"
                rel="noopener noreferrer"
                href="https://www.scpharmaceuticals.com/privacy-policy/"
                variant="subfooter"
                color={textColor}
                fontSize={[12, 12, 16]}
                aria-label="opens privacy policy page in a new tab"
              >
                {privacy}
              </Link>
            </Flex>
          </Flex>
        </Flex>
      </Section>
    </>
  );
};

export default Footer;
